import type { ApolloClient, OperationVariables } from '@apollo/client'
import type { TypedDocumentNode } from '@graphql-typed-document-node/core'
import { print } from 'graphql'
import { hash } from 'ohash'
import { reactive, unref } from 'vue'

type ApolloClients = Record<'default', ApolloClient<object>> & Record<string, ApolloClient<object>>

export const useTypedApolloClients = () => useNuxtApp().$apolloClients as ApolloClients | undefined

export const useTypedAsyncQuery = <TData, TVariables extends OperationVariables = OperationVariables>(
  document: TypedDocumentNode<TData, TVariables>,
  variables?: TVariables,
) => {
  const clients = useTypedApolloClients()
  const clientId = clients?.default ? 'default' : Object.keys(clients ?? {})[0]
  const client = clientId ? clients?.[clientId] : undefined

  if (!client) {
    throw new Error('@nuxtjs/apollo: no client found')
  }

  const queryVariables = variables ? (reactive(variables) as TVariables) : undefined
  const key = hash({ query: print(document), variables: unref(queryVariables), clientId })
  const asyncDataOptions = queryVariables ? { watch: [queryVariables] } : undefined

  return useAsyncData(
    key,
    () => client.query<TData, TVariables>({ query: document, variables: unref(queryVariables) }).then((r) => r.data),
    asyncDataOptions,
  )
}
